import { Col, Row, Table, Tooltip } from "antd";
import axios from "axios";
import moment from "moment";
import React, { useMemo, useState } from "react";
import { useQuery } from "react-query";
import DatePicker from "../../../../../components/inputs/MomentDatePicker";
import { serverUrl } from "../../../../../nestserver";
import { TYPE_META } from "./DailyActivityHome";

const { RangePicker } = DatePicker;

const authHeaders = () => ({
  Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
});

/**
 * Day-care roster × day matrix — how many meals/loo visits/naps each
 * flagged student had, per day in the chosen range. One ranged call to the
 * backend, bucketed by day client-side.
 */
const DailyActivityHistory = () => {
  const [dateRange, setDateRange] = useState([moment().subtract(6, "days"), moment()]);

  const fetchHistory = async () => {
    if (!dateRange[0] || !dateRange[1]) return { students: {} };

    const fromDate = dateRange[0].format("YYYY-MM-DD");
    const toDate = dateRange[1].format("YYYY-MM-DD");
    const res = await axios.get(
      `${serverUrl}/montessori/daily-activity/roster/history?fromDate=${fromDate}&toDate=${toDate}`,
      { headers: authHeaders() },
    );
    const rows = res.data?.result?.data || [];

    const students = {};
    rows.forEach((row) => {
      const id = row.student?._id;
      if (!id) return;
      const record = {
        _id: id,
        first_name: row.student?.first_name,
        last_name: row.student?.last_name,
        byDate: {},
      };
      row.entries.forEach((entry) => {
        const date = moment(entry.date).format("YYYY-MM-DD");
        const counts =
          record.byDate[date] || (record.byDate[date] = { meal: 0, loo: 0, sleep: 0 });
        if (counts[entry.type] !== undefined) counts[entry.type] += 1;
      });
      students[id] = record;
    });

    return { students };
  };

  const history = useQuery(
    ["daily-activity-history", dateRange[0]?.format("YYYY-MM-DD"), dateRange[1]?.format("YYYY-MM-DD")],
    fetchHistory,
    { enabled: !!dateRange[0] && !!dateRange[1] },
  );

  const students = useMemo(() => Object.values(history?.data?.students || {}), [history?.data]);

  const dayCount =
    dateRange[1] && dateRange[0] ? moment(dateRange[1]).diff(moment(dateRange[0]), "day") + 1 : 0;

  const columns = [
    {
      title: "Name",
      dataIndex: "_id",
      fixed: "left",
      width: 160,
      render: (_id, record) => (
        <div className="text-base font-medium capitalize">
          {record.first_name} {record.last_name}
        </div>
      ),
    },
    ...Array.from({ length: dayCount }).map((_, i) => {
      const date = moment(dateRange[0]).add(i, "days").format("YYYY-MM-DD");
      return {
        title: moment(date).format("DD MMM"),
        dataIndex: "_id",
        align: "center",
        width: 110,
        render: (id, record) => {
          const counts = record.byDate?.[date];
          if (!counts || (counts.meal === 0 && counts.loo === 0 && counts.sleep === 0)) {
            return <span className="text-gray-300">—</span>;
          }
          return (
            <div className="flex items-center justify-center gap-2 text-sm">
              {Object.entries(TYPE_META).map(([type, meta]) =>
                counts[type] > 0 ? (
                  <Tooltip key={type} title={`${meta.label}: ${counts[type]}`}>
                    <span className="inline-flex items-center" style={{ color: "#6b7280" }}>
                      <meta.icon size={15} />
                      <sup className="ml-0.5">{counts[type]}</sup>
                    </span>
                  </Tooltip>
                ) : null,
              )}
            </div>
          );
        },
      };
    }),
  ];

  return (
    <div className="pt-8">
      <div className="bg-white rounded-2xl border border-gray-100 p-4 sm:p-6">
        <div className="text-2xl text-orange-500 font-semibold mb-5">Day Care Activity History</div>
        {/* Filter toolbar */}
        <Row gutter={[24, 16]} align="middle" className="pb-6 mb-6 border-b border-gray-100">
          <Col xs={24} md={12} lg={8}>
            <div className="text-xs font-semibold text-orange-500 uppercase tracking-wide mb-1.5">
              Date range
            </div>
            <RangePicker
              value={dateRange}
              format="DD/MM/YYYY"
              className="w-full"
              disabledDate={(d) => !d || d.isAfter(moment().endOf("day"))}
              onChange={(dates) => dates && dates[0] && dates[1] && setDateRange(dates)}
            />
          </Col>
        </Row>

        <Table
          scroll={{ x: "max-content" }}
          className="rounded-2xl"
          columns={columns}
          dataSource={students}
          loading={history.isLoading}
          rowKey={(record) => record._id}
          pagination={{ pageSize: 20 }}
          locale={{ emptyText: "No activity in this range" }}
        />
      </div>
    </div>
  );
};

export default DailyActivityHistory;
